import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  AccountBalanceOutlined,
  Add as AddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

import CustomModal from "../../Components/CustomModal";
import CustomSnackBar from "../../Components/CustomSnackBar";
import { useAuth } from "../../context/AuthContext";
import { saleService } from "../../services";
import MainText from "../../Components/MainText";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const normalizeAccount = (source) => {
  if (!source || typeof source !== "object") return null;
  return {
    id: source.id,
    bank_name: source.bank_name || "",
    account_number: source.account_number || "",
    account_type: source.account_type || "",
    account_name: source.account_name || source.name || "",
    name: source.name || source.account_name || "",
    is_active: Boolean(source.is_active),
  };
};

function BankAccountForm({
  setOpen,
  mode = "create",
  initialValues = {
    bank_name: "",
    account_number: "",
    account_type: "",
    account_name: "",
  },
  onSubmit,
}) {
  const [values, setValues] = useState({
    bank_name: initialValues.bank_name || "",
    account_number: initialValues.account_number || "",
    account_type: initialValues.account_type || "",
    account_name: initialValues.account_name || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!values.bank_name.trim()) {
      setError("El banco es obligatorio.");
      return;
    }
    if (!values.account_number.trim()) {
      setError("El numero de cuenta es obligatorio.");
      return;
    }
    if (!values.account_name.trim()) {
      setError("El nombre de la cuenta es obligatorio.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await Promise.resolve(
        onSubmit?.({
          bank_name: values.bank_name.trim(),
          account_number: values.account_number.trim(),
          account_type: values.account_type.trim(),
          account_name: values.account_name.trim(),
        })
      );
      setOpen(false);
    } catch (submitError) {
      setError(submitError?.response?.data?.detail || "No se pudo guardar la cuenta bancaria.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={2}>
      <TextField
        label="Banco"
        size="small"
        value={values.bank_name}
        onChange={(event) => setValues((prev) => ({ ...prev, bank_name: event.target.value }))}
        autoFocus
        required
        fullWidth
      />
      <TextField
        label="Numero de cuenta"
        size="small"
        value={values.account_number}
        onChange={(event) => setValues((prev) => ({ ...prev, account_number: event.target.value }))}
        required
        fullWidth
      />
      <TextField
        label="Tipo de cuenta"
        size="small"
        value={values.account_type}
        onChange={(event) => setValues((prev) => ({ ...prev, account_type: event.target.value }))}
        fullWidth
      />
      <TextField
        label="Nombre de cuenta"
        size="small"
        value={values.account_name}
        onChange={(event) => setValues((prev) => ({ ...prev, account_name: event.target.value }))}
        required
        fullWidth
      />
      {error ? (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      ) : null}
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={() => setOpen(false)} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          {loading ? "Guardando..." : mode === "edit" ? "Guardar cambios" : "Crear cuenta"}
        </Button>
      </Stack>
    </Stack>
  );
}

export default function CompanyBankAccountPage() {
  const isMobile = useMediaQuery("(max-width:768px)");
  const { currentBranch, effectivePerms = [], isSuperuser } = useAuth();

  const branchId = currentBranch?.id || null;
  const branchName = currentBranch?.name || "";
  const canManageAccounts = isSuperuser || effectivePerms.includes("sale_manage_transfer_accounts");

  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  const loadAccounts = useCallback(async () => {
    if (!branchId) {
      setAccounts([]);
      return;
    }

    setLoading(true);
    try {
      const data = await saleService.listTransferAccounts({ branch_id: branchId });
      setAccounts(toArray(data).map(normalizeAccount).filter(Boolean));
    } catch (error) {
      setAccounts([]);
      showSnack(error?.response?.data?.detail || "No se pudieron cargar las cuentas bancarias.", "error");
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return accounts.filter((item) => {
      if (statusFilter === "active" && !item.is_active) return false;
      if (statusFilter === "inactive" && item.is_active) return false;
      if (!query) return true;

      const haystack = [
        item.bank_name,
        item.account_number,
        item.account_type,
        item.account_name,
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [accounts, search, statusFilter]);

  const handleCreate = async (payload) => {
    await saleService.createTransferAccount({
      branch_id: branchId,
      bank_name: payload.bank_name,
      account_number: payload.account_number,
      account_type: payload.account_type,
      name: payload.account_name,
      is_active: true,
    });
    showSnack("Cuenta bancaria creada correctamente.", "success");
    await loadAccounts();
  };

  const handleEdit = async (payload) => {
    if (!selectedAccount?.id) return;
    await saleService.updateTransferAccount(selectedAccount.id, {
      branch_id: branchId,
      bank_name: payload.bank_name,
      account_number: payload.account_number,
      account_type: payload.account_type,
      name: payload.account_name,
    });
    showSnack("Cuenta bancaria actualizada correctamente.", "success");
    await loadAccounts();
  };

  const handleToggleActive = useCallback(
    async (row) => {
      if (!row?.id) return;
      try {
        await saleService.updateTransferAccount(row.id, {
          branch_id: branchId,
          is_active: !row.is_active,
        });
        showSnack(row.is_active ? "Cuenta desactivada." : "Cuenta activada.", "success");
        await loadAccounts();
      } catch (error) {
        showSnack(error?.response?.data?.detail || "No se pudo actualizar el estado.", "error");
      }
    },
    [branchId, loadAccounts]
  );

  const columns = useMemo(
    () => [
      {
        field: "bank_name",
        headerName: "Banco",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "account_number",
        headerName: "Numero cuenta",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "account_type",
        headerName: "Tipo",
        minWidth: 150,
        flex: 1,
        renderCell: ({ value }) => (
          <Typography variant="body2" color={value ? "text.primary" : "text.secondary"}>
            {value || "-"}
          </Typography>
        ),
      },
      {
        field: "account_name",
        headerName: "Nombre cuenta",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "is_active",
        headerName: "Estado",
        width: 120,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            label={value ? "Activa" : "Inactiva"}
            color={value ? "success" : "default"}
            variant={value ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Acciones",
        width: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Editar">
              <span>
                <IconButton
                  size="small"
                  disabled={!canManageAccounts}
                  onClick={() => {
                    setSelectedAccount(row);
                    setEditOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={row.is_active ? "Desactivar" : "Activar"}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canManageAccounts}
                  color={row.is_active ? "error" : "success"}
                  onClick={() => handleToggleActive(row)}
                >
                  {row.is_active ? (
                    <BlockIcon fontSize="small" />
                  ) : (
                    <CheckCircleIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [canManageAccounts, handleToggleActive]
  );

  if (!canManageAccounts) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para gestionar cuentas bancarias.
        </Typography>
      </Box>
    );
  }

  if (!branchId) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin sucursal activa
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Selecciona una sucursal para administrar cuentas bancarias.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
  <MainText title={'Cuentas bancarias'} subtitle={`Administracion de cuentas para transferencias. Sucursal actual: ${branchName}`} icon={AccountBalanceOutlined} width="50%" />
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refrescar">
            <IconButton onClick={loadAccounts}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Crear cuenta
          </Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Buscar cuenta..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            label="Estado"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="active">Activas</MenuItem>
            <MenuItem value="inactive">Inactivas</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ height: isMobile ? "calc(100vh - 280px)" : "calc(100vh - 290px)", width: "100%" }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          density={isMobile ? "compact" : "standard"}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#757B2B",
              color: "#ffffff",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover":
              {
                backgroundColor: "#757B2B !important",
              },
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator":
              {
                color: "#ffffff !important",
              },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(16, 23, 60, 0.08)",
            },
          }}
        />
      </Box>

      <CustomModal
        open={createOpen}
        setOpen={setCreateOpen}
        title="Crear cuenta bancaria"
        component={BankAccountForm}
        componentProps={{
          mode: "create",
          initialValues: {
            bank_name: "",
            account_number: "",
            account_type: "",
            account_name: "",
          },
          onSubmit: handleCreate,
        }}
        width={isMobile ? "92%" : "520px"}
      />

      <CustomModal
        open={editOpen}
        setOpen={setEditOpen}
        title="Editar cuenta bancaria"
        component={BankAccountForm}
        componentProps={{
          mode: "edit",
          initialValues: {
            bank_name: selectedAccount?.bank_name || "",
            account_number: selectedAccount?.account_number || "",
            account_type: selectedAccount?.account_type || "",
            account_name: selectedAccount?.account_name || "",
          },
          onSubmit: handleEdit,
        }}
        width={isMobile ? "92%" : "520px"}
      />

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}
